import React, { Component } from 'react'
import {
    Card, Table, Button, Modal, Form, Input, Select, Tag, Space,
    Popconfirm, message, Row, Col, Typography, Badge, Empty
} from 'antd'
import {
    PlusOutlined, EditOutlined, ApiOutlined, CheckCircleOutlined,
    StopOutlined, ReloadOutlined, RocketOutlined, ArrowRightOutlined
} from '@ant-design/icons'
import {
    getMcpServiceList, saveMcpService, updateMcpService, deleteMcpService
} from '../../request'

const { Text, Paragraph } = Typography
const { TextArea } = Input

const STATUS_MAP = {
    enabled: { text: '启用', color: 'success', icon: <CheckCircleOutlined /> },
    disabled: { text: '停用', color: 'default', icon: <StopOutlined /> },
    1: { text: '启用', color: 'success', icon: <CheckCircleOutlined /> },
    0: { text: '停用', color: 'default', icon: <StopOutlined /> },
}

export default class McpService extends Component {
    formRef = React.createRef()

    state = {
        dataSource: [],
        columns: [],
        loading: false,
        submitting: false,
        modalVisible: false,
        editRecord: null,
        filterKeyword: '',
        filterStatus: undefined,
    }

    componentDidMount() {
        this.setState({ columns: this.buildColumns() })
        this.loadList()
    }

    buildColumns = () => [
        {
            title: '服务',
            key: 'service',
            width: 320,
            render: (_, record) => {
                const installCommandMeta = this.getInstallCommandMeta(record)

                return (
                    <div>
                        <Space size={8} style={{ marginBottom: 6, flexWrap: 'wrap' }}>
                            <Text strong style={{ fontSize: 14 }}>{record.serviceName || '-'}</Text>
                            <Text code style={{ fontSize: 11 }}>{record.serviceCode}</Text>
                        </Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.description || '暂无描述，建议补充服务边界和承载能力说明'}
                        </Text>
                        <div style={{
                            marginTop: 10,
                            padding: '10px 12px',
                            borderRadius: 10,
                            background: '#f8fafc',
                            border: '1px solid #e5edf7',
                        }}>
                            <div style={{ marginBottom: 6 }}>
                                <Text strong style={{ fontSize: 12, color: '#175ec9' }}>安装命令</Text>
                            </div>
                            {installCommandMeta.copyable ? (
                                <Paragraph
                                    copyable={{ text: installCommandMeta.text, tooltips: ['复制命令', '已复制'] }}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Text code style={{ fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                                        {installCommandMeta.text}
                                    </Text>
                                </Paragraph>
                            ) : (
                                <>
                                    <Paragraph style={{ marginBottom: 6 }}>
                                        <Text code style={{ fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#98a2b3' }}>
                                            {installCommandMeta.text}
                                        </Text>
                                    </Paragraph>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        请先生成 API Key，再复制安装命令。
                                    </Text>
                                </>
                            )}
                        </div>
                    </div>
                )
            },
        },
        {
            title: '工作台概览',
            key: 'overview',
            width: 240,
            render: (_, record) => {
                const status = STATUS_MAP[record.status] || STATUS_MAP[0]
                return (
                    <div>
                        <Space size={8} style={{ marginBottom: 8, flexWrap: 'wrap' }}>
                            <Tag color={status.color} icon={status.icon}>{status.text}</Tag>
                            <Badge count={record.toolCount || 0} showZero style={{ backgroundColor: record.toolCount ? '#175ec9' : '#d9d9d9' }} />
                            <Text type="secondary">已关联工具</Text>
                        </Space>
                        <div>
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => this.goToTools(record)}>
                                查看该服务下的工具
                            </Button>
                        </div>
                    </div>
                )
            },
        },
        {
            title: '时间',
            key: 'time',
            width: 180,
            render: (_, record) => (
                <div>
                    <div style={{ marginBottom: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>更新时间</Text>
                    </div>
                    <Text>{record.updatedAt || record.createdAt || '-'}</Text>
                </div>
            ),
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 220,
            render: (_, record) => (
                <Space size={6} wrap>
                    <Button size="small" type="primary" ghost icon={<ArrowRightOutlined />} onClick={() => this.goToTools(record)}>
                        进入工具台
                    </Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => this.openEdit(record)}>
                        编辑
                    </Button>
                    <Popconfirm title="确认停用该服务？" onConfirm={() => this.handleDelete(record)}>
                        <Button size="small" danger icon={<StopOutlined />}>
                            停用
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ]

    loadList = () => {
        this.setState({ loading: true })
        return getMcpServiceList({})
            .then(res => this.setState({ dataSource: Array.isArray(res) ? res : (res?.list || []) }))
            .catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    getMcpGatewayBaseUrl = () => 'https://mcpgateway.lechun.cc'

    getServiceInstallUrl = (record, apiKey = '<YOUR_API_KEY>') => {
        const serviceCode = record?.serviceCode || '<service-code>'
        return `${this.getMcpGatewayBaseUrl()}/mcp/${serviceCode}?key=${apiKey}`
    }

    getInstallCommandMeta = (record) => {
        if (record?.installCommand) {
            return {
                text: record.installCommand,
                copyable: true,
                hasPlaceholderKey: false,
            }
        }

        const serviceCode = record?.serviceCode || '<service-code>'
        const endpoint = this.getServiceInstallUrl(record)
        return {
            text: `codex mcp add ${serviceCode} --url '${endpoint}'`,
            copyable: true,
            hasPlaceholderKey: true,
        }
    }

    getFilteredData = () => {
        const { dataSource, filterKeyword, filterStatus } = this.state
        return dataSource.filter(item => {
            if (filterStatus !== undefined && item.status !== filterStatus) return false
            if (!filterKeyword) return true
            const keyword = filterKeyword.toLowerCase()
            return (item.serviceCode || '').toLowerCase().includes(keyword)
                || (item.serviceName || '').toLowerCase().includes(keyword)
                || (item.description || '').toLowerCase().includes(keyword)
        })
    }

    goToTools = (record) => {
        this.props.history.push(`/admin/mcptool?serviceId=${record.id}`)
    }

    goToAllTools = () => {
        this.props.history.push('/admin/mcptool')
    }

    clearFilters = () => {
        this.setState({
            filterKeyword: '',
            filterStatus: undefined,
        })
    }

    openCreate = () => {
        this.setState({ modalVisible: true, editRecord: null })
        setTimeout(() => {
            if (this.formRef.current) {
                this.formRef.current.resetFields()
            }
        }, 0)
    }

    openEdit = (record) => {
        this.setState({ modalVisible: true, editRecord: record })
        setTimeout(() => {
            if (this.formRef.current) {
                this.formRef.current.setFieldsValue({
                    serviceCode: record.serviceCode,
                    serviceName: record.serviceName,
                    description: record.description,
                    status: record.status ?? 'enabled',
                })
            }
        }, 0)
    }

    handleSubmit = async (values) => {
        const { editRecord } = this.state
        this.setState({ submitting: true })
        try {
            await (editRecord
                ? updateMcpService({ ...values, id: editRecord.id })
                : saveMcpService(values))
            message.success(editRecord ? '更新成功' : '创建成功')
            this.setState({ modalVisible: false, editRecord: null })
            await this.loadList()
            if (!editRecord) {
                const createdItem = this.state.dataSource.find(item => item.serviceCode === values.serviceCode)
                if (!createdItem) {
                    message.warning('服务已提交成功，但列表刷新结果中暂未看到新服务，请检查列表接口是否返回了最新数据')
                }
            }
        } catch (e) {
            // request layer already handles error toast
        } finally {
            this.setState({ submitting: false })
        }
    }

    handleDelete = (record) => {
        deleteMcpService({ id: record.id })
            .then(() => { message.success('已停用'); this.loadList() })
            .catch(() => {})
    }

    renderStats = (dataSource) => {
        const summary = dataSource.reduce((acc, item) => {
            acc.total += 1
            if (item.status === 'enabled' || item.status === 1) acc.active += 1
            acc.toolTotal += item.toolCount || 0
            return acc
        }, { total: 0, active: 0, toolTotal: 0 })

        const cards = [
            { title: '服务总数', value: summary.total, color: '#175ec9', icon: <ApiOutlined /> },
            { title: '启用中服务', value: summary.active, color: '#389e0d', icon: <CheckCircleOutlined /> },
            { title: '已挂载工具', value: summary.toolTotal, color: '#722ed1', icon: <RocketOutlined /> },
        ]

        return (
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                {cards.map(card => (
                    <Col xs={24} sm={12} xl={8} key={card.title}>
                        <Card bordered={false} style={{
                            borderRadius: 16,
                            border: `1px solid ${card.color}22`,
                            background: `linear-gradient(180deg, ${card.color}12 0%, #ffffff 70%)`,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 12,
                                    background: `${card.color}18`,
                                    color: card.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 18,
                                }}>
                                    {card.icon}
                                </div>
                                <div>
                                    <div style={{ fontSize: 12, color: '#667085', marginBottom: 4 }}>{card.title}</div>
                                    <div style={{ fontSize: 28, fontWeight: 700, color: card.color }}>{card.value}</div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>
        )
    }

    render() {
        const { columns, loading, submitting, modalVisible, editRecord, filterKeyword, filterStatus, dataSource } = this.state
        const filteredData = this.getFilteredData()

        return (
            <div style={{ padding: '0 4px' }}>
                <div style={{ marginBottom: 20, padding: '0 10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 700, color: '#101828' }}>MCP 服务管理</div>
                            <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
                                管理和发布基于 Model Context Protocol 的服务实例，为 AI 模型提供统一的能力集接入点与安全认证。
                            </div>
                        </div>
                        <Space>
                            <Button icon={<RocketOutlined />} onClick={this.goToAllTools}>
                                全部工具列表
                            </Button>
                        </Space>
                    </div>
                </div>
                {this.renderStats(dataSource)}

                <Card
                    bordered={false}
                    style={{ borderRadius: 18, border: '1px solid #edf1f7' }}
                    title={(
                        <Space>
                            <ApiOutlined style={{ color: '#175ec9' }} />
                            <span style={{ fontWeight: 600 }}>服务聚合视图</span>
                            <Text type="secondary" style={{ fontSize: 12 }}>共 {filteredData.length} 个服务</Text>
                        </Space>
                    )}
                    extra={(
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={this.loadList}>刷新</Button>
                            <Button type="primary" icon={<PlusOutlined />} onClick={this.openCreate}>新建服务</Button>
                        </Space>
                    )}
                >
                    <div style={{
                        display: 'flex',
                        gap: 8,
                        marginBottom: 16,
                        flexWrap: 'wrap',
                        padding: 14,
                        borderRadius: 14,
                        background: '#f8fafc',
                    }}>
                        <Select
                            style={{ width: 140 }}
                            placeholder="状态"
                            allowClear
                            value={filterStatus}
                            onChange={(value) => this.setState({ filterStatus: value })}
                            options={[
                                { label: '启用', value: 'enabled' },
                                { label: '停用', value: 'disabled' },
                            ]}
                        />
                        <Input.Search
                            style={{ width: 260 }}
                            placeholder="搜索服务名称/编码/描述"
                            value={filterKeyword}
                            allowClear
                            onChange={(e) => this.setState({ filterKeyword: e.target.value })}
                        />
                        <Button onClick={this.clearFilters}>清空筛选</Button>
                    </div>

                    <Table
                        bordered
                        size="middle"
                        rowKey="id"
                        dataSource={filteredData}
                        columns={columns}
                        loading={loading}
                        scroll={{ x: 960 }}
                        pagination={{ size: 'small', pageSize: 20, showSizeChanger: true }}
                        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前筛选下还没有服务，可以先创建一个服务分组" /> }}
                    />
                </Card>

                <Modal
                    title={(
                        <Space>
                            {editRecord ? <EditOutlined /> : <PlusOutlined />}
                            {editRecord ? '编辑服务' : '新建 MCP 服务'}
                        </Space>
                    )}
                    visible={modalVisible}
                    onCancel={() => this.setState({ modalVisible: false, editRecord: null })}
                    footer={null}
                    destroyOnClose
                    width={560}
                >
                    <div style={{
                        marginBottom: 16,
                        padding: 12,
                        borderRadius: 12,
                        background: '#f8fafc',
                        border: '1px solid #edf1f7',
                    }}>
                        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.7 }}>
                            服务只维护分组信息和边界描述。详细的输入、输出、版本和发布动作请在工具工作台完成。
                        </Text>
                    </div>
                    <Form ref={this.formRef} layout="vertical" onFinish={this.handleSubmit} initialValues={{ status: 'enabled' }}>
                        <Form.Item
                            name="serviceCode"
                            label="服务编码"
                            rules={[
                                { required: true, message: '请输入服务编码' },
                                { pattern: /^[a-zA-Z0-9_-]+$/, message: '只允许字母、数字、下划线、连字符' },
                            ]}
                        >
                            <Input placeholder="例如：bi_service" disabled={!!editRecord} prefix={<Text type="secondary" style={{ fontSize: 12 }}>code:</Text>} />
                        </Form.Item>
                        <Form.Item name="serviceName" label="服务名称" rules={[{ required: true, message: '请输入服务名称' }]}>
                            <Input placeholder="例如：BI 数据服务" maxLength={50} />
                        </Form.Item>
                        <Form.Item name="description" label="服务描述">
                            <TextArea rows={4} placeholder="描述这个服务聚合了哪些工具、主要面向什么场景" maxLength={200} showCount />
                        </Form.Item>
                        <Form.Item name="status" label="状态">
                            <Select options={[{ label: '启用', value: 'enabled' }, { label: '停用', value: 'disabled' }]} style={{ width: 140 }} />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                            <Space>
                                <Button onClick={() => this.setState({ modalVisible: false, editRecord: null })}>取消</Button>
                                <Button type="primary" htmlType="submit" loading={submitting}>
                                    {editRecord ? '保存' : '创建'}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        )
    }
}
